import { Note, Review } from './items';
import { CareerCard } from './careerCard';

export interface EditState<T> {
  hasUnsavedChanges: boolean;
  originalContent: T;
  currentContent: T;
  lastSaved?: string; // ISO timestamp for potential auto-save feature
  isSaving?: boolean;  // Track if currently saving
  saveError?: string;  // Track any save errors
}

export type NoteEditState = EditState<Note>;
export type ReviewEditState = EditState<Review>;
export type CareerCardEditState = EditState<CareerCard>;

export type AnyEditState = NoteEditState | ReviewEditState | CareerCardEditState;

// Type guard functions to help with type checking
export const isNoteEditState = (state: AnyEditState | null): state is NoteEditState => {
  if (!state?.originalContent) return false;
  return 'type' in state.originalContent && state.originalContent.type === 'note';
};

export const isReviewEditState = (state: AnyEditState | null): state is ReviewEditState => {
  if (!state?.originalContent) return false;
  return 'type' in state.originalContent && state.originalContent.type === 'review';
};

export const isCareerCardEditState = (state: AnyEditState | null): state is CareerCardEditState => {
  if (!state?.originalContent) return false;
  return 'card_data' in state.originalContent;
};

// Helper to get the title of any edit state for display in dialogs
export const getEditStateTitle = (state: AnyEditState): string => {
  const content = state.currentContent;
  if ('card_data' in content) {
    return content.card_data.title || 'Untitled Card';
  }
  return content.title || 'Untitled';
};

// Helper to determine if we should show unsaved changes warning
export const shouldShowUnsavedWarning = (state: AnyEditState | null): boolean => {
  return Boolean(state?.hasUnsavedChanges);
};

// Helper to determine if any edit state is currently saving
export const isAnySaving = (states: (AnyEditState | null)[]): boolean => {
  return states.some(state => state?.isSaving);
}; 